import type { components } from '../../api/schema.gen.ts';
import { apiBlob, apiFetch } from '../../api/http.ts';

/**
 * Server shapes, aliased from the generated OpenAPI types rather than restated.
 *
 * A hand-written copy would compile happily while silently disagreeing with the API —
 * which is exactly what generation exists to prevent. Renaming or retyping a property on
 * the server now surfaces here as a type error.
 *
 * `version` is monotonic per item and is what lets the client drop stale echoes and
 * order events that arrive out of sequence.
 */
type Schemas = components['schemas'];

export type Item = Schemas['ItemResponse'];
export type ItemImage = Schemas['ItemImageResponse'];
export type ItemImageCatalogue = Schemas['ItemImageCatalogueResponse'];
export type AisleCatalogue = Schemas['AisleCatalogueResponse'];
export type ListItemsSnapshot = Schemas['ListItemsResponse'];
export type Suggestion = Schemas['SuggestionResponse'];
export type ShoppingList = Schemas['ListResponse'];

export const itemKeys = {
  list: (listId: string) => ['items', listId] as const,

  /** Every list's items — used when a change can span lists, such as forgetting a name. */
  all: ['items'] as const,
  suggestions: (listId: string) => ['suggestions', listId] as const,

  /**
   * Every list's suggestions.
   *
   * Chips are ranked from the *group's* whole history and exclude whatever is on the
   * list being viewed, so adding, renaming, deleting or clearing an item on one list
   * changes the chips on all of them. Invalidating this prefix refetches the list
   * currently on screen and marks the rest stale, so switching list shows the truth
   * rather than a cached row from before the change.
   */
  allSuggestions: ['suggestions'] as const,

  /**
   * The group's photo catalogue. Keyed by group, not by list, because a photo belongs to
   * a name and every list in the household shares it.
   */
  images: (groupId: string) => ['item-images', groupId] as const,

  /** Every group's photos — invalidated by the hub, which does not name a group. */
  allImages: ['item-images'] as const,

  /**
   * The group's aisle corrections and aisle order. Keyed by group for the reason photos
   * are: a correction belongs to a name, and every list in the household shares it.
   *
   * One key for both because a client holding the corrections without the order cannot
   * draw the list, so there is nothing to be gained by letting them go stale separately.
   */
  aisles: (groupId: string) => ['aisles', groupId] as const,

  /** Every group's aisles — invalidated by the hub, which does not name a group. */
  allAisles: ['aisles'] as const,
};

export const listKeys = {
  ofGroup: (groupId: string) => ['lists', groupId] as const,

  /**
   * Every group's lists. Used by the reconcile on reconnect, which does not know which
   * group is showing — and should not have to, since a list created or renamed in
   * another household is just as stale as one in this one.
   */
  all: ['lists'] as const,
};

export function getLists(groupId: string): Promise<ShoppingList[]> {
  return apiFetch<ShoppingList[]>(`/api/groups/${groupId}/lists`);
}

export function createList(groupId: string, name: string): Promise<ShoppingList> {
  return apiFetch<ShoppingList>(`/api/groups/${groupId}/lists`, {
    method: 'POST',
    // The add-time settings — assignNewItemsToCreator, preserveItemNotes — are left to
    // the server's defaults rather than sent, so there is one place that decides what a
    // brand new list does.
    json: { name, isDefault: false },
  });
}

export function updateList(
  listId: string,
  update: {
    name: string;
    isDefault: boolean;
    assignNewItemsToCreator: boolean;
    preserveItemNotes: boolean;
    groupByAisle: boolean;
  },
): Promise<ShoppingList> {
  return apiFetch<ShoppingList>(`/api/lists/${listId}`, { method: 'PUT', json: update });
}

export function deleteList(listId: string): Promise<void> {
  return apiFetch<void>(`/api/lists/${listId}`, { method: 'DELETE' });
}

export function getItems(listId: string, since?: string): Promise<ListItemsSnapshot> {
  const query = since ? `?since=${encodeURIComponent(since)}` : '';
  return apiFetch<ListItemsSnapshot>(`/api/lists/${listId}/items${query}`);
}

export function getSuggestions(listId: string): Promise<Suggestion[]> {
  return apiFetch<Suggestion[]>(`/api/lists/${listId}/items/suggestions`);
}

/**
 * The id is generated by the caller, not the server. That is what makes a retry after a
 * dropped connection safe: the same id collides instead of adding the milk twice.
 */
export function addItem(
  listId: string,
  item: { id: string; name: string; note?: string },
): Promise<Item> {
  return apiFetch<Item>(`/api/lists/${listId}/items`, { method: 'POST', json: item });
}

export function checkItem(itemId: string): Promise<Item> {
  return apiFetch<Item>(`/api/items/${itemId}/check`, { method: 'POST' });
}

export function uncheckItem(itemId: string): Promise<Item> {
  return apiFetch<Item>(`/api/items/${itemId}/uncheck`, { method: 'POST' });
}

/**
 * Name, note and assignment travel together because the editor changes them together.
 * `assignedToUserId` has no "leave it alone" value — null means unassigned — so every
 * caller must send the assignment it wants, not the one it happens to remember.
 */
export function updateItem(
  itemId: string,
  update: {
    name: string;
    note?: string | null;
    assignedToUserId?: string | null;
    sortOrder?: number;
  },
): Promise<Item> {
  return apiFetch<Item>(`/api/items/${itemId}`, { method: 'PUT', json: update });
}

export function deleteItem(itemId: string): Promise<Item> {
  return apiFetch<Item>(`/api/items/${itemId}`, { method: 'DELETE' });
}

/**
 * Which of the group's names have a photo.
 *
 * A photo belongs to a name rather than to a row, so this is one small group-scoped
 * query the list joins against locally — the same shape as suggestions. Putting it on
 * each item instead would mean attaching one photo had to bump the version of every item
 * with that name on every list.
 */
export function getItemImages(groupId: string): Promise<ItemImageCatalogue> {
  return apiFetch<ItemImageCatalogue>(`/api/groups/${groupId}/item-images`);
}

/**
 * Attaches or replaces the photo for this item's name.
 *
 * The image id is minted by the caller, as item ids are and for the same reason: an
 * attach queued in a dead spot and replayed after its response was lost has to land on
 * the same row, or the first id is orphaned under a URL the browser has already cached.
 *
 * The blob goes as the raw body. `apiFetch` passes `body` straight through and sets no
 * Content-Type of its own when `json` is absent, which is exactly what is wanted here.
 */
export function attachItemImage(
  itemId: string,
  imageId: string,
  blob: Blob,
): Promise<Schemas['AttachImageResponse']> {
  return apiFetch<Schemas['AttachImageResponse']>(`/api/items/${itemId}/image/${imageId}`, {
    method: 'PUT',
    body: blob,
    headers: { 'content-type': blob.type },
  });
}

export function removeItemImage(itemId: string): Promise<void> {
  return apiFetch<void>(`/api/items/${itemId}/image`, { method: 'DELETE' });
}

/**
 * The bytes of one photo, fetched with the bearer token attached.
 *
 * An `<img src>` pointing at the API would arrive unauthenticated and 401, so the caller
 * fetches here and renders an object URL instead. The id is immutable — replacing a photo
 * mints a new one — so the result never needs revalidating.
 */
export function getItemImageBlob(groupId: string, imageId: string): Promise<Blob> {
  return apiBlob(`/api/groups/${groupId}/item-images/${imageId}`);
}

/**
 * The household's aisle corrections, and every shop it walks.
 *
 * Note what is <b>not</b> here: which aisle anything is actually in. That comes from
 * `aisles.json`, shipped with the bundle and resolved by `aisleFor`, so a row can be filed
 * the instant it is typed with no network at all. This endpoint carries only the places
 * where the household has overruled that table — usually none, often a handful.
 *
 * Nor is which shop <i>you</i> are in. That is a preference held in this browser and never
 * sent, which is what lets two people shop the same list in two different shops at once —
 * so every store comes back and the client picks. See `useSelectedStore`.
 */
export function getAisles(groupId: string): Promise<AisleCatalogue> {
  return apiFetch<AisleCatalogue>(`/api/groups/${groupId}/aisles`);
}

/**
 * Says where this item's <i>name</i> belongs, for the whole household and for good.
 *
 * Item-scoped rather than name-scoped because the caller has an item and the server
 * derives the name from it inside the same statement — so a rename landing at the same
 * moment cannot file the correction against the wrong name.
 */
export function setItemAisle(itemId: string, aisle: string): Promise<Schemas['ItemAisleResponse']> {
  return apiFetch<Schemas['ItemAisleResponse']>(`/api/items/${itemId}/aisle`, {
    method: 'PUT',
    json: { aisle },
  });
}

/** Undoes a correction, putting the name back under whatever the shipped table says. */
export function clearItemAisle(itemId: string): Promise<void> {
  return apiFetch<void>(`/api/items/${itemId}/aisle`, { method: 'DELETE' });
}

/**
 * Reorders the aisles to match the way one shop is laid out.
 *
 * Sends every code, always. The server cleans rather than validates — unknown codes go,
 * missing ones are appended — so a partial order is survivable, but sending one would mean
 * the household's arrangement quietly depended on which bundle they happened to be running.
 */
export function setAisleOrder(storeId: string, order: string[]): Promise<AisleCatalogue> {
  return apiFetch<AisleCatalogue>(`/api/stores/${storeId}/order`, {
    method: 'PUT',
    json: { order },
  });
}

/**
 * Adds a shop.
 *
 * All four store calls answer with the whole catalogue rather than with the one row they
 * touched, because the client holds a single cache entry for the lot — and because the
 * switcher, the settings panel and the sections on the list are all drawn from it.
 */
export function createStore(groupId: string, name: string): Promise<AisleCatalogue> {
  return apiFetch<AisleCatalogue>(`/api/groups/${groupId}/stores`, {
    method: 'POST',
    json: { name },
  });
}

export function renameStore(storeId: string, name: string): Promise<AisleCatalogue> {
  return apiFetch<AisleCatalogue>(`/api/stores/${storeId}`, {
    method: 'PUT',
    json: { name },
  });
}

/** Removes a shop and the order it held. The household's last one is refused. */
export function deleteStore(storeId: string): Promise<AisleCatalogue> {
  return apiFetch<AisleCatalogue>(`/api/stores/${storeId}`, { method: 'DELETE' });
}

export function clearChecked(listId: string): Promise<string[]> {
  return apiFetch<string[]>(`/api/lists/${listId}/items/clear-checked`, { method: 'POST' });
}
